import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Users, UserPlus, UserMinus, ChefHat, 
  BookOpen, Loader2 
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { followService } from '../../services/followService';
import { RecipeCard } from './RecipeCard';

export const ChefProfile = ({ 
  chefId, 
  currentUserId, 
  onBack, 
  onOpenRecipe,
  userLikes = [],
  onLike,
  settings 
}) => {
  const [profile, setProfile] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('recipes');
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);

  useEffect(() => {
    if (chefId) {
      fetchProfile();
      fetchRecipes();
      if (currentUserId && currentUserId !== chefId) {
        checkFollowStatus();
      }
    }
  }, [chefId, currentUserId]);

  const checkFollowStatus = async () => {
    try {
      const following = await followService.checkFollowStatus(currentUserId, chefId);
      setIsFollowing(following);
    } catch (e) {
      console.error('Error checking follow status:', e);
    }
  };

  const fetchProfile = async () => {
    try {
      const profileData = await followService.getPublicProfile(chefId);
      if (profileData) {
        setProfile(profileData);
      }
    } catch (e) {
      console.error('Error fetching profile:', e);
    }
  };

  const fetchRecipes = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('recipes')
        .select('*, profiles:author_id(display_name, avatar_url)')
        .eq('author_id', chefId)
        .eq('is_public', true)
        .order('created_at', { ascending: false });

      if (data && !error) {
        setRecipes(data.map(r => ({
          ...r,
          author: r.profiles?.display_name || 'Chef',
          author_avatar: r.profiles?.avatar_url
        })));
      }
    } catch (e) {
      console.error('Error fetching recipes:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFollowers = async () => {
    try {
      const data = await followService.getFollowers(chefId);
      setFollowers(data);
    } catch (e) {
      console.error('Error fetching followers:', e);
    }
  };

  const fetchFollowing = async () => {
    try {
      const data = await followService.getFollowing(chefId);
      setFollowing(data);
    } catch (e) {
      console.error('Error fetching following:', e);
    }
  };

  const handleFollow = async () => {
    if (!currentUserId || currentUserId === chefId) return;
    
    setIsFollowLoading(true);
    try {
      if (isFollowing) {
        await followService.unfollowUser(currentUserId, chefId);
        setIsFollowing(false);
        setProfile(prev => prev ? {
          ...prev,
          followers_count: Math.max(0, (prev.followers_count || 0) - 1)
        } : null);
      } else {
        await followService.followUser(currentUserId, chefId);
        setIsFollowing(true);
        setProfile(prev => prev ? {
          ...prev,
          followers_count: (prev.followers_count || 0) + 1
        } : null);
      }
    } catch (e) {
      console.error('Error toggling follow:', e);
      alert(e.message || 'Error al procesar la acción');
    } finally {
      setIsFollowLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'followers' && followers.length === 0) fetchFollowers();
    if (tab === 'following' && following.length === 0) fetchFollowing();
  };

  if (!profile) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-primary" size={48} />
      </div>
    );
  }

  return (
    <div data-testid="chef-profile" className="animate-in fade-in duration-500">
      {/* Back Button */}
      <button 
        onClick={onBack}
        className="mb-8 flex items-center gap-2 text-[10px] font-black uppercase text-stone-400 hover:text-primary transition-colors"
      >
        <ArrowLeft size={16}/> Volver
      </button>

      {/* Profile Header */}
      <div className="bg-white dark:bg-stone-900 rounded-[48px] border border-stone-100 dark:border-stone-800 p-8 md:p-12 mb-12 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-60 h-60 bg-primary/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 flex flex-col md:flex-row items-center md:items-start gap-8">
          {/* Avatar */}
          <img 
            src={profile.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${chefId}`}
            className="w-32 h-32 md:w-40 md:h-40 rounded-[40px] object-cover border-4 border-white dark:border-stone-800 shadow-2xl"
            alt={profile.display_name}
          />
          
          {/* Info */}
          <div className="flex-1 text-center md:text-left">
            <h1 className="text-4xl md:text-5xl font-black heading-serif text-stone-900 dark:text-white mb-2">
              {profile.display_name}
            </h1>
            <p className="text-stone-500 dark:text-stone-400 font-bold mb-4">
              @{profile.username}
            </p>
            
            {profile.bio && (
              <p className="text-stone-600 dark:text-stone-300 italic mb-6 max-w-lg">
                "{profile.bio}"
              </p>
            )}
            
            {/* Stats */}
            <div className="flex items-center justify-center md:justify-start gap-8 mb-6">
              <button 
                onClick={() => handleTabChange('recipes')}
                className={`text-center transition-colors ${activeTab === 'recipes' ? 'text-primary' : ''}`}
              >
                <p className="text-2xl font-black dark:text-white">{profile.recipes_count || 0}</p>
                <p className="text-[10px] font-black uppercase tracking-widest text-stone-400">Recetas</p>
              </button>
              <button 
                onClick={() => handleTabChange('followers')}
                className={`text-center transition-colors ${activeTab === 'followers' ? 'text-primary' : ''}`}
              >
                <p className="text-2xl font-black dark:text-white">{profile.followers_count || 0}</p>
                <p className="text-[10px] font-black uppercase tracking-widest text-stone-400">Seguidores</p>
              </button>
              <button 
                onClick={() => handleTabChange('following')}
                className={`text-center transition-colors ${activeTab === 'following' ? 'text-primary' : ''}`}
              >
                <p className="text-2xl font-black dark:text-white">{profile.following_count || 0}</p>
                <p className="text-[10px] font-black uppercase tracking-widest text-stone-400">Siguiendo</p>
              </button>
            </div>
            
            {/* Follow Button */}
            {currentUserId && currentUserId !== chefId && (
              <button
                data-testid="follow-btn"
                onClick={handleFollow}
                disabled={isFollowLoading}
                className={`px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all flex items-center gap-2 shadow-xl ${
                  isFollowing
                    ? 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 hover:bg-rose-50 hover:text-rose-500'
                    : 'bg-primary text-white shadow-primary/30 hover:scale-105'
                }`}
              >
                {isFollowLoading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : isFollowing ? (
                  <><UserMinus size={16} /> Dejar de seguir</>
                ) : (
                  <><UserPlus size={16} /> Seguir</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content Tabs */}
      {activeTab === 'recipes' && (
        <div>
          <h2 className="text-2xl font-black heading-serif dark:text-white mb-8 flex items-center gap-3">
            <BookOpen size={24} className="text-primary" />
            Recetas de {profile.display_name}
          </h2>
          
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-72 bg-stone-100 dark:bg-stone-800 rounded-[32px] animate-pulse" />
              ))}
            </div>
          ) : recipes.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {recipes.map(recipe => (
                <RecipeCard 
                  key={recipe.id}
                  recipe={recipe}
                  onOpen={onOpenRecipe}
                  onLike={onLike}
                  isLiked={userLikes.includes(recipe.id)}
                  settings={settings}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-stone-50 dark:bg-stone-900/50 rounded-[40px] border-2 border-dashed border-stone-100 dark:border-stone-800">
              <ChefHat size={48} className="mx-auto mb-4 text-stone-200" />
              <p className="text-stone-500 font-bold">Este chef aún no ha publicado recetas</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'followers' && (
        <UserList 
          users={followers} 
          title="Seguidores" 
          emptyMessage="Este chef aún no tiene seguidores"
          currentUserId={currentUserId}
        />
      )}

      {activeTab === 'following' && (
        <UserList 
          users={following} 
          title="Siguiendo" 
          emptyMessage="Este chef aún no sigue a nadie"
          currentUserId={currentUserId}
        />
      )}
    </div>
  );
};

const UserList = ({ users, title, emptyMessage, currentUserId }) => (
  <div>
    <h2 className="text-2xl font-black heading-serif dark:text-white mb-8 flex items-center gap-3">
      <Users size={24} className="text-primary" />
      {title}
    </h2>
    
    {users.length > 0 ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map(user => (
          <div 
            key={user.id}
            className="bg-white dark:bg-stone-900 p-6 rounded-[32px] border border-stone-100 dark:border-stone-800 flex items-center gap-4"
          >
            <img 
              src={user.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.id}`}
              className="w-14 h-14 rounded-2xl object-cover"
              alt={user.display_name}
            />
            <div className="flex-1 min-w-0">
              <p className="font-black text-stone-900 dark:text-white truncate">
                {user.display_name}
              </p>
              <p className="text-sm text-stone-400 truncate">@{user.username}</p>
              <p className="text-[10px] text-stone-400 mt-1">
                {user.followers_count || 0} seguidores
              </p>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div className="text-center py-16 bg-stone-50 dark:bg-stone-900/50 rounded-[40px] border-2 border-dashed border-stone-100 dark:border-stone-800">
        <Users size={48} className="mx-auto mb-4 text-stone-200" />
        <p className="text-stone-500 font-bold">{emptyMessage}</p>
      </div>
    )}
  </div>
);

export default ChefProfile;
